import { FocusMonitor } from '@angular/cdk/a11y';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import {
    Component,
    ElementRef,
    HostBinding,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    Optional,
    Self,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { ControlValueAccessor, FormBuilder, FormControl, FormGroup, NgControl } from '@angular/forms';
import { MAT_FORM_FIELD, MatFormField, MatFormFieldControl } from '@angular/material/form-field';
import { Subject } from 'rxjs';

import { DURATION_UNITS_ORDER, Duration, DurationFormat, Unit, getUnitSeparator } from './se-duration.utils';

export type FormType = {
    [k in keyof DurationFormat]?: FormControl<string | null>;
};

export type InputsConfiguration = {
    [k in keyof DurationFormat]-?: {
        size: number | null;
        separator: string | null;
    };
};

@Component({
    selector: 'se-duration',
    templateUrl: 'se-duration.component.html',
    styleUrls: ['se-duration.component.scss'],
    providers: [{ provide: MatFormFieldControl, useExisting: SeDurationComponent }],
})
export class SeDurationComponent implements ControlValueAccessor, MatFormFieldControl<number>, OnChanges, OnDestroy {
    // Necessario per legare la label al controllo
    static nextId = 0;

    //#region ViewChildren

    @ViewChild('wrapper')
    wrapperElement?: ElementRef<HTMLElement>;

    @ViewChild('months')
    monthsInput?: ElementRef<HTMLInputElement>;

    @ViewChild('days')
    daysInput?: ElementRef<HTMLInputElement>;

    @ViewChild('hours')
    hoursInput?: ElementRef<HTMLInputElement>;

    @ViewChild('minutes')
    minutesInput?: ElementRef<HTMLInputElement>;

    @ViewChild('seconds')
    secondsInput?: ElementRef<HTMLInputElement>;

    //#endregion

    //#region MatFormFieldControl implementation

    stateChanges = new Subject<void>();
    focused = false;
    touched = false;
    controlType = 'se-duration';

    @HostBinding('id')
    id = `${this.controlType}-${SeDurationComponent.nextId++}`;

    // Il controllo è vuoto se per ogni unità configurata il relativo controllo è vuoto o 0.
    get empty() {
        const {
            value: { months, days, hours, minutes, seconds },
        } = this.units;

        return (
            (!this.format.months || !months) &&
            (!this.format.days || !days) &&
            (!this.format.hours || !hours) &&
            (!this.format.minutes || !minutes) &&
            (!this.format.seconds || !seconds)
        );
    }

    @HostBinding('class.floating')
    get shouldLabelFloat() {
        return this.focused || !this.empty;
    }

    // eslint-disable-next-line @angular-eslint/no-input-rename
    @Input('aria-describedby')
    userAriaDescribedBy?: string;

    @Input()
    get placeholder(): string {
        return this._placeholder;
    }
    set placeholder(value: string) {
        this._placeholder = value;
        this.stateChanges.next();
    }
    private _placeholder = '';

    @Input()
    get required(): boolean {
        return this._required;
    }
    set required(value: BooleanInput) {
        this._required = coerceBooleanProperty(value);
        this.stateChanges.next();
    }
    private _required = false;

    @Input()
    get disabled(): boolean {
        return this._disabled;
    }
    set disabled(value: BooleanInput) {
        this._disabled = coerceBooleanProperty(value);
        this._disabled ? this.units.disable() : this.units.enable();
        this.stateChanges.next();
    }
    private _disabled = false;

    @Input()
    get value(): number | null {
        if (this.units.valid) {
            const { months, days, hours, minutes, seconds } = this.units.value;

            return new Duration(this.format, {
                months: parseInt(months || '0', 10),
                days: parseInt(days || '0', 10),
                hours: parseInt(hours || '0', 10),
                minutes: parseInt(minutes || '0', 10),
                seconds: parseInt(seconds || '0', 10),
            }).asSeconds();
        }
        return null;
    }
    set value(value: unknown) {
        if (typeof value === 'string') {
            value = parseInt(value, 10);
        }
        if (typeof value === 'number') {
            const duration = Duration.fromSeconds(value, this.format);
            const { months, days, hours, minutes, seconds } = duration;
            const {
                months: oldMonths,
                days: oldDays,
                hours: oldHours,
                minutes: oldMinutes,
                seconds: oldSeconds,
            } = this.units.value;
            this.units.setValue({
                months: this.toViewValue(months, oldMonths),
                days: this.toViewValue(days, oldDays),
                hours: this.toViewValue(hours, oldHours),
                minutes: this.toViewValue(minutes, oldMinutes),
                seconds: this.toViewValue(seconds, oldSeconds),
            });
        } else {
            this.units.setValue({
                months: null,
                days: null,
                hours: null,
                minutes: null,
                seconds: null,
            });
        }
        this.stateChanges.next();
    }

    get errorState(): boolean {
        return this.units.invalid && this.touched;
    }

    setDescribedByIds(ids: string[]) {
        this.wrapperElement?.nativeElement.setAttribute('aria-describedby', ids.join(' '));
    }

    //#endregion

    @Input()
    format: DurationFormat = { minutes: true, seconds: true };

    units: FormGroup<FormType> = this.fb.group<FormType>(
        {
            months: this.fb.control(''),
            days: this.fb.control(''),
            hours: this.fb.control(''),
            minutes: this.fb.control(''),
            seconds: this.fb.control(''),
        },
        {
            validators: this.required
                ? [
                      /* TODO: Add custom validator */
                  ]
                : [],
        },
    );

    inputsConfiguration = this.buildInputsConfiguration();

    constructor(
        private fb: FormBuilder,
        private focusMonitor: FocusMonitor,
        private elementRef: ElementRef<HTMLElement>,
        @Optional() @Inject(MAT_FORM_FIELD) public formField: MatFormField,
        @Optional() @Self() public ngControl: NgControl,
    ) {
        if (this.ngControl != null) {
            this.ngControl.valueAccessor = this;
        }
    }

    //#region Angular lifecycle methods

    ngOnChanges({ format }: SimpleChanges): void {
        if (format) {
            this.inputsConfiguration = this.buildInputsConfiguration();
        }
    }

    ngOnDestroy() {
        this.stateChanges.complete();
        this.focusMonitor.stopMonitoring(this.elementRef);
    }

    //#endregion

    //#region ControlValueAccessor implementation

    onChange = (_: any) => {};
    onTouched = () => {};

    writeValue(value: unknown): void {
        if (typeof value === 'number') {
            this.value = value;
        } else if (typeof value === 'string' && !isNaN(parseInt(value, 10))) {
            this.value = parseInt(value, 10);
        } else {
            this.value = null;
        }
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    //#endregion

    //#region Event handlers

    onFocusIn(_: FocusEvent) {
        if (!this.focused) {
            this.focused = true;
            this.stateChanges.next();
        }
    }

    onFocusOut(event: FocusEvent) {
        if (!this.elementRef.nativeElement.contains(event.relatedTarget as Element)) {
            this.touched = true;
            this.focused = false;
            this.onTouched();
            this.stateChanges.next();
        }
    }

    onContainerClick(event: MouseEvent) {
        if (event.target instanceof HTMLInputElement) {
            return;
        }

        const target = this.getClosestInput(event.clientX, event.clientY);
        if (target) {
            this.focusMonitor.focusVia(target, 'program');
        }
    }

    onInput(element?: ElementRef<HTMLInputElement>): void {
        if (!element) {
            return;
        }
        if (element.nativeElement.value?.length >= element.nativeElement.size) {
            this.focusNext(element.nativeElement);
        }
        this.onChange(this.value);
    }

    onKeyup(event: KeyboardEvent) {
        if (!(event.target instanceof HTMLInputElement)) {
            return;
        }
        const element = event.target;
        switch (event.key) {
            case 'ArrowUp':
                this.increment(element, 1);
                break;
            case 'ArrowDown':
                this.increment(element, -1);
                break;
            case 'ArrowLeft':
            case 'Backspace':
                if (element.selectionStart === element.selectionEnd && element.selectionStart === 0) {
                    this.focusPrev(element);
                }
                break;
            case 'ArrowRight':
            case 'Delete':
                if (
                    element.selectionStart === element.selectionEnd &&
                    element.selectionStart === element.value?.length
                ) {
                    this.focusNext(element);
                }
                break;
        }
    }

    //#endregion

    //#region Private methods

    private buildInputsConfiguration() {
        const config: InputsConfiguration = {
            months: { separator: null, size: 1 },
            days: { separator: null, size: 1 },
            hours: { separator: null, size: 1 },
            minutes: { separator: null, size: 1 },
            seconds: { separator: null, size: 1 },
        };

        // Recupera le unità attive nel formato, ordinate.
        const formatUnits: Unit[] = (Object.keys(this.format) as Unit[]).sort(
            (a, b) => DURATION_UNITS_ORDER[a] - DURATION_UNITS_ORDER[b],
        );

        // Per ogni unità, imposta il separatore e la larghezza del campo di input.
        for (let i = 0; i < formatUnits.length; ++i) {
            const unit = formatUnits[i];
            // Non impostare il separatore per l'ultima unità.
            const separator = i == formatUnits.length - 1 ? null : getUnitSeparator(unit, formatUnits[i + 1]);
            // Il primo campo è leggermente più largo.
            const size = i == 0 ? 3 : 2;
            config[unit] = { separator, size };
        }

        return config;
    }

    private focusNext(element: HTMLInputElement) {
        const target = this.getNextInput(element);
        if (target) {
            this.focusMonitor.focusVia(target, 'program');
        }
    }

    private focusPrev(element: HTMLInputElement): void {
        const target = this.getPreviousInput(element);

        if (target) {
            this.focusMonitor.focusVia(target, 'program');
        }
    }

    private getClosestInput(x: number, y: number): ElementRef<HTMLInputElement> {
        return [this.monthsInput, this.daysInput, this.hoursInput, this.minutesInput, this.secondsInput]
            .filter(((input) => !!input) as (x: any) => x is ElementRef<HTMLInputElement>)
            .map((input) => {
                const rect = input.nativeElement.getBoundingClientRect();
                const dx = Math.max(0, rect.left - x, x - rect.right);
                const dy = Math.max(0, rect.top - y, y - rect.bottom);
                return [input, Math.sqrt(dx * dx + dy * dy)] as const;
            })
            .sort((a, b) => a[1] - b[1])[0][0];
    }

    private getNextInput(element: HTMLInputElement) {
        if (element === this.monthsInput?.nativeElement) {
            return this.daysInput;
        } else if (element === this.daysInput?.nativeElement) {
            return this.hoursInput;
        } else if (element === this.hoursInput?.nativeElement) {
            return this.minutesInput;
        } else if (element === this.minutesInput?.nativeElement) {
            return this.secondsInput;
        }
        return null;
    }

    private getPreviousInput(element: HTMLInputElement) {
        if (element === this.daysInput?.nativeElement) {
            return this.monthsInput;
        } else if (element === this.hoursInput?.nativeElement) {
            return this.daysInput;
        } else if (element === this.minutesInput?.nativeElement) {
            return this.hoursInput;
        } else if (element === this.secondsInput?.nativeElement) {
            return this.minutesInput;
        }
        return null;
    }

    private increment(element: HTMLInputElement, amount: number) {
        let value = this.value ?? 0;
        if (element === this.monthsInput?.nativeElement) {
            value += amount * 30 * 24 * 60 * 60;
        } else if (element === this.daysInput?.nativeElement) {
            value += amount * 24 * 60 * 60;
        } else if (element === this.hoursInput?.nativeElement) {
            value += amount * 60 * 60;
        } else if (element === this.minutesInput?.nativeElement) {
            value += amount * 60;
        } else if (element === this.secondsInput?.nativeElement) {
            value += amount;
        }

        this.value = value;
        this.onChange(this.value);
    }

    private toViewValue(value: number, viewValue: string | null | undefined): string | null {
        if (value == 0 && !viewValue) {
            return null;
        }
        return value.toString();
    }

    //#endregion
}
